import React, { useMemo, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import CustomModal from '../../reports/pages/components/CustomModal'
import SingleSelect from '../../reports/components/SingleSelect'
import { useGetDevicesWithAutomationsQuery } from '../api/automationsApi'
import { useGetDeviceGroupListQuery } from '../../settings/api/api'
import { Flex, Collapse, Button, Checkbox } from '@chakra-ui/react'
import { useGetAllLocationsQuery } from '../../reports/api/reportsApi'

export default function FilterModal({
  isOpen,
  handleClose,
  groupId,
  hasAutomation,
  deviceId,
  locationId,
  setGroupId,
  setDeviceId,
  setLocationId,
  setHasAutomation,
}) {
  const { handleSubmit } = useForm()
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [selectedHasAutomation, setSelectedHasAutomation] = useState(false)

  const { data: devicesGroups } = useGetDeviceGroupListQuery({}, { skip: !isOpen })
  const { data: locations } = useGetAllLocationsQuery({}, { skip: !isOpen })
  const { data: userDevices } = useGetDevicesWithAutomationsQuery(
    { location: selectedLocation, groups: selectedGroup },
    { skip: !isOpen },
  )

  useEffect(() => {
    if (isOpen) {
      setSelectedHasAutomation(hasAutomation)
      setSelectedLocation(locationId)
      setSelectedGroup(groupId)
      setSelectedDevice(deviceId)
    }
  }, [isOpen, hasAutomation, locationId, groupId, deviceId])

  const isFilterSelected = useMemo(
    () =>
      locationId ||
      groupId ||
      deviceId ||
      selectedDevice ||
      selectedGroup ||
      selectedLocation ||
      selectedHasAutomation,
    [
      locationId,
      groupId,
      deviceId,
      selectedLocation,
      selectedGroup,
      selectedDevice,
      selectedHasAutomation,
    ],
  )

  const locationselect = useMemo(
    () =>
      locations?.results.map(({ id, title }) => ({
        id,
        name: title,
      })),
    [locations],
  )

  const userDevicesSelect = useMemo(
    () =>
      userDevices?.map(({ id, title }) => ({
        id,
        name: title,
      })),
    [userDevices],
  )

  const devicesGroupsSelect = useMemo(() => {
    if (!devicesGroups) return []

    return devicesGroups
      .filter((group) => !selectedLocation || group.location === selectedLocation)
      .map(({ id, name }) => ({
        id,
        name,
      }))
  }, [devicesGroups, selectedLocation])

  const handleFilter = () => {
    if (selectedDevice) setDeviceId(selectedDevice)
    if (selectedGroup) setGroupId(selectedGroup)
    if (selectedLocation) setLocationId(selectedLocation)
    if (selectedHasAutomation) setHasAutomation(selectedHasAutomation)
    handleClose()
  }

  const handleClear = () => {
    setSelectedDevice(null)
    setSelectedGroup(null)
    setSelectedLocation(null)
    setGroupId(null)
    setDeviceId(null)
    setHasAutomation(false)
    setLocationId(null)
    handleClose()
  }

  return (
    <CustomModal
      title="Filters"
      tooltipLabel="Please populate the required fields first"
      handleSend={handleSubmit(handleFilter)}
      isDisabled={false}
      isLoading={false}
      isOpen={isOpen}
      onClose={handleClose}
      buttonName="Filter"
      resetButton={
        <Collapse in={!!isFilterSelected}>
          <Button onClick={handleClear} variant="ghost" w="full">
            Reset filters
          </Button>
        </Collapse>
      }
    >
      <Flex direction="column" gap={3}>
        <SingleSelect
          header="Location"
          items={locationselect}
          setSelectedOption={(selectedOptionId) => {
            setSelectedLocation(selectedOptionId)
            setSelectedGroup(null)
          }}
          selectedOption={selectedLocation}
        />
        <SingleSelect
          header="Group"
          items={devicesGroupsSelect}
          setSelectedOption={(selectedOptionId) => {
            setSelectedGroup(selectedOptionId)
            setSelectedDevice(null)
          }}
          selectedOption={selectedGroup}
        />
        <SingleSelect
          header="Device"
          items={userDevicesSelect}
          setSelectedOption={(selectedOptionId) => setSelectedDevice(selectedOptionId)}
          selectedOption={selectedDevice}
        />
        <Checkbox
          isChecked={selectedHasAutomation}
          onChange={(e) => setSelectedHasAutomation(e.target.checked)}
        >
          Show devices with automations
        </Checkbox>
      </Flex>
    </CustomModal>
  )
}
